import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ContentMix, MixPreset, PostRole } from "@marketing/shared";
import { api, unwrap } from "@/lib/api";

export type GalleryTemplate = {
  key: string;
  name: string;
  category: "holiday" | "sale" | "launch" | "engagement" | "evergreen";
  /** True for a template specific to this brand's country. */
  local: boolean;
  description: string | null;
  postCountDefault: number;
  stepCount: number;
  roles: PostRole[];
  suggestedWindow: { startDate: string; endDate: string } | null;
};

export type CampaignSummary = {
  id: string;
  name: string;
  templateKey: string | null;
  startDate: string;
  endDate: string;
  postCount: number;
  status: string;
  createdAt: string;
  _count: { posts: number };
  /** Live statuses, because the stored `status` above never moves. */
  posts: Array<{ status: string }>;
};

export type Shortfall = { requested: number; planned: number; reason: string } | null;

export type CampaignPreview = {
  posts: Array<{ role: string; guidance: string; day: string; index: number }>;
  postsConsumed: number;
  pageCount: number;
  shortfall: Shortfall;
  templateName: string | null;
};

export type MonthPreview = {
  month: string;
  posts: Array<{ bucket: string; role: string; day: string; index: number }>;
  socialAccountIds: string[];
  postsConsumed: number;
  campaignDays: string[];
  shortfall: Shortfall;
};

export type MonthlyPlanSummary = {
  id: string;
  month: string;
  postCount: number;
  contentMix: ContentMix;
  status: string;
  _count: { posts: number };
  /** Live statuses, because the stored `status` above never moves. */
  posts: Array<{ status: string }>;
};

export type GenerationResult = {
  generated: number;
  failed: number;
  scheduled: number;
  approvalSent: { sent: number; failed: number; count: number } | null;
  errors: string[];
};

export function useTemplates(brandId: string | null) {
  return useQuery({
    queryKey: ["campaign-templates", brandId],
    queryFn: () =>
      unwrap<GalleryTemplate[]>(api.get(`/campaigns/brands/${brandId}/templates`)),
    enabled: !!brandId,
  });
}

export function useCampaigns(brandId: string | null) {
  return useQuery({
    queryKey: ["campaigns", brandId],
    queryFn: () => unwrap<CampaignSummary[]>(api.get(`/campaigns/brands/${brandId}/campaigns`)),
    enabled: !!brandId,
  });
}

export function useMixPresets() {
  return useQuery({
    queryKey: ["mix-presets"],
    queryFn: () => unwrap<MixPreset[]>(api.get("/campaigns/mix-presets")),
    staleTime: Infinity,
  });
}

/**
 * Dry run before committing.
 *
 * Spec §9 requires showing how many posts a campaign will use before it runs,
 * and this calls the same planner the real thing does — so the number shown is
 * the number charged.
 */
export function usePreviewCampaign() {
  return useMutation({
    mutationFn: (input: {
      brandId: string;
      templateKey: string | null;
      startDate: string;
      endDate: string;
      postCount: number;
      socialAccountIds: string[];
    }) => unwrap<CampaignPreview>(api.post("/campaigns/campaigns/preview", input)),
  });
}

export function useCreateCampaign(brandId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      unwrap<{ id: string; postCount: number; postsConsumed: number; shortfall: Shortfall }>(
        api.post("/campaigns/campaigns", input),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaigns", brandId] }),
  });
}

export function useGenerateCampaign(brandId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (campaignId: string) =>
      unwrap<GenerationResult>(api.post(`/campaigns/campaigns/${campaignId}/generate`)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaigns", brandId] });
      qc.invalidateQueries({ queryKey: ["calendar"] });
      qc.invalidateQueries({ queryKey: ["brands"] });
    },
  });
}

export function useCancelCampaign(brandId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (campaignId: string) => api.delete(`/campaigns/campaigns/${campaignId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["campaigns", brandId] }),
  });
}

export function useMonthlyPlans(brandId: string | null) {
  return useQuery({
    queryKey: ["monthly-plans", brandId],
    queryFn: () =>
      unwrap<MonthlyPlanSummary[]>(api.get(`/campaigns/brands/${brandId}/monthly-plans`)),
    enabled: !!brandId,
  });
}

export function usePreviewMonth() {
  return useMutation({
    mutationFn: (input: {
      brandId: string;
      month: string;
      postCount: number;
      contentMix: ContentMix;
      /** Empty = every connected page. Must match what create sends. */
      socialAccountIds?: string[];
    }) => unwrap<MonthPreview>(api.post("/campaigns/monthly-plans/preview", input)),
  });
}

export function useCreateMonthlyPlan(brandId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      unwrap<{ id: string; postCount: number; postsConsumed: number; shortfall: Shortfall }>(
        api.post("/campaigns/monthly-plans", input),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["monthly-plans", brandId] }),
  });
}

export function useGenerateMonthlyPlan(brandId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planId: string) =>
      unwrap<GenerationResult>(api.post(`/campaigns/monthly-plans/${planId}/generate`)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["monthly-plans", brandId] });
      qc.invalidateQueries({ queryKey: ["calendar"] });
      qc.invalidateQueries({ queryKey: ["brands"] });
    },
  });
}

export function useCancelMonthlyPlan(brandId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (planId: string) => api.delete(`/campaigns/monthly-plans/${planId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["monthly-plans", brandId] }),
  });
}
